import type { DatePrecision, ModelRelease } from "../types/model";
import type { Language } from "./localization";
import { localeFor, ui } from "./localization";

const DATE_FORMATTERS = {
  en: {
    longDay: new Intl.DateTimeFormat("en", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC"
    }),
    shortDay: new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC"
    }),
    month: new Intl.DateTimeFormat("en", {
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    }),
    weekday: new Intl.DateTimeFormat("en", {
      weekday: "long",
      timeZone: "UTC"
    })
  },
  zh: {
    longDay: new Intl.DateTimeFormat("zh-CN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC"
    }),
    shortDay: new Intl.DateTimeFormat("zh-CN", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: "UTC"
    }),
    month: new Intl.DateTimeFormat("zh-CN", {
      year: "numeric",
      month: "long",
      timeZone: "UTC"
    }),
    weekday: new Intl.DateTimeFormat("zh-CN", {
      weekday: "long",
      timeZone: "UTC"
    })
  }
} satisfies Record<
  Language,
  Record<"longDay" | "shortDay" | "month" | "weekday", Intl.DateTimeFormat>
>;

function parseExactDate(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

export function formatDate(
  value: string | null,
  precision: DatePrecision,
  compact = false,
  language: Language = "en"
): string {
  if (!value) return ui(language, "dateUnknown");
  const formatter = DATE_FORMATTERS[language];
  if (precision === "day") {
    return (compact ? formatter.shortDay : formatter.longDay).format(
      parseExactDate(value)
    );
  }
  if (precision === "month") {
    return formatter.month.format(parseExactDate(`${value}-01`));
  }
  if (precision === "year") return value;
  return value;
}

export function formatReleaseDate(
  record: ModelRelease,
  compact = false,
  language: Language = "en"
): string {
  return formatDate(
    record.dates.release ?? record.dates.releaseRaw,
    record.dates.precision,
    compact,
    language
  );
}

export function dateGroupLabel(
  record: ModelRelease,
  language: Language = "en"
): {
  key: string;
  title: string;
  subtitle: string;
} {
  const raw = record.dates.release ?? record.dates.releaseRaw;
  const precision = record.dates.precision;
  if (!raw || precision === "unknown") {
    return {
      key: "unknown",
      title: ui(language, "dateUnknown"),
      subtitle: ui(language, "verificationPending")
    };
  }
  if (precision === "day") {
    const date = parseExactDate(raw);
    const today = new Date();
    const todayKey = today.toISOString().slice(0, 10);
    const yesterday = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1)
    )
      .toISOString()
      .slice(0, 10);
    return {
      key: `day:${raw}`,
      title:
        raw === todayKey
          ? ui(language, "today")
          : raw === yesterday
            ? ui(language, "yesterday")
            : DATE_FORMATTERS[language].shortDay.format(date),
      subtitle: DATE_FORMATTERS[language].weekday.format(date)
    };
  }
  return {
    key: `${precision}:${raw}`,
    title: formatDate(raw, precision, false, language),
    subtitle:
      precision === "month"
        ? ui(language, "monthPrecision")
        : ui(language, "yearPrecision")
  };
}

export function vendorInitials(company: string): string {
  const parts = company
    .replace(/\([^)]*\)/g, " ")
    .split(/[\s/·-]+/)
    .filter(Boolean);
  if (!parts.length) return "AI";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
}

export function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

export function formatCount(
  count: number,
  unit: "model" | "record" | "release",
  language: Language = "en"
): string {
  const formatted = count.toLocaleString(localeFor(language));
  if (language === "zh") {
    return unit === "model"
      ? `${formatted} 个模型`
      : unit === "record"
        ? `${formatted} 条记录`
        : `${formatted} 次发布`;
  }
  return `${formatted} ${count === 1 ? unit : `${unit}s`}`;
}
